(() => {
  const LOADER_URL =
    'https://js.sentry-cdn.com/d377e11828e43247888f8ea20f9ebcc7.min.js';
  const DSN =
    'https://d377e11828e43247888f8ea20f9ebcc7@o960144.ingest.us.sentry.io/4509910181281792';
  const FIRST_PARTY_SAMPLE_RATE = 0.1;
  const THIRD_PARTY_SAMPLE_RATE = 0.01;
  const TRACES_SAMPLE_RATE = 0.15;
  const CONSOLE_SAMPLE_RATE = FIRST_PARTY_SAMPLE_RATE;
  const CONSOLE_MESSAGE_MAX_LENGTH = 200;
  const CONSOLE_FALLBACK_MESSAGE = 'console.error';
  const REBIRTH_ASSET_PATH = '/assets/rebirth-';
  const { pageType, template, locale } = document.currentScript?.dataset ?? {};

  const isLiveTheme = () => window.Shopify?.theme?.role === 'main';

  const markShopifyThemeFramesAsInApp = event => {
    const values = event?.exception?.values || [];
    values.forEach(value => {
      const frames = value?.stacktrace?.frames;
      if (!Array.isArray(frames)) return;
      frames.forEach(frame => {
        const filename = String(frame?.filename || '');
        frame.in_app =
          filename.includes('/cdn/shop/') ||
          filename.includes('cdn.shopify.com') ||
          filename.startsWith(window.location.origin);
      });
    });
  };

  const isFirstPartyEvent = event => {
    const lastFrame = (event?.exception?.values || [])
      .map(value => {
        const frames = value?.stacktrace?.frames || [];
        return frames[frames.length - 1];
      })
      .reverse()
      .find(Boolean);
    return Boolean(lastFrame?.in_app);
  };

  const getStackFrameLines = error => {
    if (!error?.stack) return [];
    const stack = String(error.stack);
    const header = String(error);
    const frames = stack.startsWith(header)
      ? stack.slice(header.length)
      : stack;
    return frames.split('\n');
  };

  const findConsoleCaller = hint =>
    getStackFrameLines(hint?.syntheticException).find(
      line => /https?:\/\//.test(line) && !line.includes('sentry-cdn.com')
    );

  const isRebirthConsoleCall = hint =>
    Boolean(findConsoleCaller(hint)?.includes(REBIRTH_ASSET_PATH));

  const pickStrings = args =>
    Array.isArray(args) ? args.filter(arg => typeof arg === 'string') : [];

  const joinConsoleStrings = (strings, fallback) =>
    strings.join(' ') || fallback;

  const keepStringArguments = event => {
    const strings = pickStrings(event?.extra?.arguments);
    if (event?.extra) event.extra.arguments = strings;
    return strings;
  };

  const beforeBreadcrumb = breadcrumb => {
    if (breadcrumb?.category !== 'console') return breadcrumb;
    const strings = pickStrings(breadcrumb.data?.arguments);
    breadcrumb.data = { ...breadcrumb.data, arguments: strings };
    breadcrumb.message = joinConsoleStrings(
      strings,
      breadcrumb.level ? `console.${breadcrumb.level}` : 'console'
    );
    return breadcrumb;
  };

  const replaceJoinedMessage = (event, hint, strings) => {
    if (hint?.originalException instanceof Error) return;
    const message = joinConsoleStrings(strings, CONSOLE_FALLBACK_MESSAGE);
    event.message = message;
    const syntheticValue = event.exception?.values?.[0];
    if (syntheticValue) syntheticValue.value = message;
  };

  const beforeSendConsole = (event, hint) => {
    if (!isRebirthConsoleCall(hint)) return null;

    markShopifyThemeFramesAsInApp(event);

    const strings = keepStringArguments(event);
    replaceJoinedMessage(event, hint, strings);
    const consoleMessage = strings[0]?.replace(/\s+/g, ' ').trim();

    event.tags = event.tags || {};
    event.tags.error_origin = 'first-party';
    event.tags.error_source = 'console';
    if (consoleMessage) {
      event.tags.console_message = consoleMessage.slice(
        0,
        CONSOLE_MESSAGE_MAX_LENGTH
      );
    }

    return Math.random() < CONSOLE_SAMPLE_RATE ? event : null;
  };

  const beforeSend = (event, hint) => {
    if (!isLiveTheme()) return null;

    if (event?.logger === 'console') return beforeSendConsole(event, hint);

    const errorMessage = event?.exception?.values?.[0]?.value || '';
    const frames = event?.exception?.values?.[0]?.stacktrace?.frames || [];

    if (
      errorMessage.includes('importScripts') &&
      errorMessage.includes('web-pixel')
    ) {
      return null;
    }

    if (errorMessage === 'Script error.' && !frames.length) return null;

    if (/^ResizeObserver loop/.test(errorMessage)) return null;

    markShopifyThemeFramesAsInApp(event);

    const firstParty = isFirstPartyEvent(event);

    event.tags = event.tags || {};
    event.tags.error_origin = firstParty ? 'first-party' : 'third-party';

    if (!firstParty) {
      if (
        errorMessage.includes('Non-Error promise rejection captured with value')
      ) {
        return null;
      }
      if (/Object Not Found Matching Id/.test(errorMessage)) return null;
    }

    const sampleRate = firstParty
      ? FIRST_PARTY_SAMPLE_RATE
      : THIRD_PARTY_SAMPLE_RATE;

    return Math.random() < sampleRate ? event : null;
  };

  const addConsoleCapture = async () => {
    const captureConsole = await window.Sentry.lazyLoadIntegration(
      'captureConsoleIntegration'
    );
    window.Sentry.addIntegration(captureConsole({ levels: ['error'] }));
  };

  window.sentryOnLoad = () => {
    window.Sentry.init({
      dsn: DSN,
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 0,
      tracesSampleRate: isLiveTheme() ? TRACES_SAMPLE_RATE : 0,
      attachStacktrace: true,
      denyUrls: [
        /^chrome-extension:\/\//i,
        /^moz-extension:\/\//i,
        /^safari-extension:\/\//i,
        /^safari-web-extension:\/\//i
      ],
      initialScope: {
        tags: {
          page_type: pageType,
          template,
          locale
        }
      },
      beforeSend,
      beforeBreadcrumb
    });
    addConsoleCapture().catch(() => {});
  };

  const insertLoader = () => {
    const loader = document.createElement('script');
    loader.async = true;
    loader.src = LOADER_URL;
    loader.crossOrigin = 'anonymous';
    loader.className = 'optanon-category-C0001';
    document.head.append(loader);
  };

  if (window.Shopify?.theme || document.readyState !== 'loading') {
    insertLoader();
  } else {
    document.addEventListener('DOMContentLoaded', insertLoader, {
      once: true
    });
  }
})();
